import type { MockMessage, MockMessageButton } from "@/dummy";
import { AI_ASSISTANT_NAME } from "@/lib/ai-assistant";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";
import {
  normalizeBackendMedia,
  normalizeBackendSources,
} from "../tabs/messages-tab/message-mapping";
import { isBlankMessage } from "../tabs/messages-tab/message-rows";

/**
 * Map a realtime SSE event to the widget's message shape for the conversation
 * buffer, or `null` when it isn't an appendable inbound bubble (a visitor's own
 * echo, a typing ping, a read-receipt, a reaction, or any event with nothing to
 * show: it would render as an empty bubble). Shared by BOTH realtime streams — the
 * main pump (inline, for main-chat inbounds + the launcher/unread side-effects
 * it also runs) and the second per-topic-thread stream (below) — so a bubble
 * reads identically whichever thread delivered it.
 *
 * Decision (blanked edit): an edit (`update`) whose content is blank (the BE blanks a message it
 * deleted) maps to `null` like any blank event, so the row keeps its PREVIOUS text. The widget has
 * no "deleted message" state, and an empty bubble is never rendered. NOTE: keep this in sync with
 * the main pump's inline mapping; they intentionally produce the same shape.
 */
export function mapRealtimeInbound(
  event: RealtimeMessageEvent,
  aiName: string = AI_ASSISTANT_NAME,
): MockMessage | null {
  // Non-message signals are handled by their own paths and NEVER become a
  // bubble. `status` in particular (a conversation-status change) carries no
  // body — without this guard it rendered as an empty bubble (and chimed).
  if (
    event.kind === "typing" ||
    event.kind === "read-receipt" ||
    event.kind === "assignment" ||
    event.kind === "status" ||
    event.kind === "blocked" ||
    event.kind === "reaction"
  ) {
    return null;
  }
  const senderKind = (event.from ?? event.sender)?.kind;
  if (senderKind === "visitor") return null; // own echo — never a new bubble
  const sender = event.from ?? event.sender;
  const buttons: MockMessageButton[] | undefined = Array.isArray(event.buttons)
    ? event.buttons.map((b) => ({
        text: b.label,
        payload: b.value,
        ...(b.type ? { type: b.type.toLowerCase() } : {}),
        ...(b.url ? { url: b.url } : {}),
        ...(b.phoneNumber ? { phoneNumber: b.phoneNumber } : {}),
      }))
    : undefined;
  const displaySenderName =
    senderKind === "ai" || senderKind === "bot" ? aiName : (sender?.name ?? "");
  const sentAtIso = event.sentAt ?? new Date().toISOString();
  const ageLabel = (() => {
    try {
      return new Date(sentAtIso).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  })();
  const rawMedia = (event as { media?: unknown }).media;
  const media = normalizeBackendMedia(rawMedia);
  const sources = normalizeBackendSources(event.sources);
  const caption =
    rawMedia && typeof rawMedia === "object" && !Array.isArray(rawMedia)
      ? (rawMedia as { caption?: unknown }).caption
      : undefined;
  const message: MockMessage = {
    id: event.externalMessageId,
    direction: "OUTBOUND",
    text: event.text || (typeof caption === "string" ? caption : ""),
    ageLabel,
    sender: {
      kind: (senderKind ?? "operator") as MockMessage["sender"]["kind"],
      name: displaySenderName,
      ...(sender?.avatar ? { avatar: sender.avatar } : {}),
    },
    ...(media.length > 0 ? { media } : {}),
    ...(sources.length > 0 ? { sources } : {}),
    ...(buttons ? { buttons } : {}),
    deliveryStatus: "DELIVERED" as const,
  };
  // Location / unknown kinds with no text, media or quick replies have nothing to render.
  if (isBlankMessage(message)) return null;
  return message;
}
